import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import {
  usePosts,
  useCreatePost,
  useDeletePost,
  useComments,
  useCreateComment,
  useToggleLike,
} from "../../../hooks/useCommunity";
import type { Post, Comment } from "../../../server/community";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { toast } from "../../../components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { logger } from "../../../lib/logger";
import { getUserMessage } from "../../../lib/errors";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getPosts } from "../../../server/community";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/community/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.community.posts.list({ limit: 10, page: 0 }),
      queryFn: async () => {
        const result = await getPosts({
          data: { limit: 10, page: 0, accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return { posts: result.posts, count: result.count };
      },
      staleTime: STALE_TIMES.realtime,
    });
  },
  pendingComponent: () => <PageSkeleton rows={6} variant="cards" />,
  errorComponent: createRouteError("Community"),
  component: CommunityPage,
});

const POSTS_PER_PAGE = 10;

function CommunityPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return (
    <CommunityContent
      userId={user.id}
      accessToken={session.access_token}
    />
  );
}

function CommunityContent({
  userId,
  accessToken,
}: {
  userId: string;
  accessToken: string;
}) {
  const [page, setPage] = useState(0);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { data, isLoading, error, refetch } = usePosts({
    limit: POSTS_PER_PAGE,
    page,
    accessToken,
  });
  const createPostMutation = useCreatePost();
  const deletePostMutation = useDeletePost();
  const toggleLikeMutation = useToggleLike();

  const posts = data?.posts ?? [];
  const count = data?.count ?? 0;
  const hasMore = posts.length < count;

  return (
      <main className="container mx-auto p-6 max-w-3xl space-y-6">
        {showCreateForm && (
          <CreatePostForm
            userId={userId}
            accessToken={accessToken}
            onSuccess={() => {
              setShowCreateForm(false);
              refetch();
            }}
            createMutation={createPostMutation}
          />
        )}

        {isLoading && posts.length === 0 ? (
          <div className="flex justify-center py-12">
            <p className="text-muted-foreground">Loading posts…</p>
          </div>
        ) : error ? (
          <Card className="p-6">
            <p className="text-destructive mb-4">
              {error instanceof Error ? error.message : "Failed to load posts"}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </Card>
        ) : posts.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground mb-2">No posts yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Be the first to create a post in the community.
            </p>
            <Button onClick={() => setShowCreateForm(true)}>Create post</Button>
          </Card>
        ) : (
          <>
            <div className="space-y-4">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  userId={userId}
                  accessToken={accessToken}
                  isExpanded={expandedPostId === post.id}
                  onToggleExpand={() =>
                    setExpandedPostId((id) => (id === post.id ? null : post.id))
                  }
                  onDeleted={() => refetch()}
                  deleteMutation={deletePostMutation}
                  toggleLikeMutation={toggleLikeMutation}
                />
              ))}
            </div>
            <div className="flex items-center justify-between pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} · {posts.length} of {count}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasMore}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </main>
  );
}

function CreatePostForm({
  userId,
  accessToken,
  onSuccess,
  createMutation,
}: {
  userId: string;
  accessToken: string;
  onSuccess: () => void;
  createMutation: ReturnType<typeof useCreatePost>;
}) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    createMutation.mutate(
      {
        title: title.trim(),
        content: content.trim(),
        userId,
        accessToken,
      },
      {
        onSuccess: () => {
          setTitle("");
          setContent("");
          onSuccess();
        },
        onError: (err) => {
          logger.error("Failed to create post", { error: err });
          toast.error(getUserMessage(err));
        },
      }
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <CardHeader>
          <h2 className="text-lg font-semibold text-card-foreground">
            Create a post
          </h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea
            placeholder="What's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
          />
        </CardContent>
        <CardFooter>
          <Button
            type="submit"
            disabled={!title.trim() || !content.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? "Posting…" : "Post"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function PostCard({
  post,
  userId,
  accessToken,
  isExpanded,
  onToggleExpand,
  onDeleted,
  deleteMutation,
  toggleLikeMutation,
}: {
  post: Post;
  userId: string;
  accessToken: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDeleted: () => void;
  deleteMutation: ReturnType<typeof useDeletePost>;
  toggleLikeMutation: ReturnType<typeof useToggleLike>;
}) {
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [userLiked, setUserLiked] = useState(post.user_has_liked ?? false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isAuthor = post.user_id === userId;

  const handleLike = () => {
    toggleLikeMutation.mutate(
      { postId: post.id, userId, accessToken },
      {
        onSuccess: (result) => {
          if (result?.liked !== undefined) {
            setUserLiked(result.liked);
            setLikesCount((c) => (result.liked ? c + 1 : c - 1));
          }
        },
      }
    );
  };

  const handleDelete = () => {
    if (!isAuthor) return;
    setShowDeleteDialog(true);
  };

  const authorName =
    post.author?.full_name || post.author?.username || "Someone";
  const date = post.created_at
    ? new Date(post.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3 pb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-card-foreground">
            {authorName}
          </p>
          <p className="text-xs text-muted-foreground">{date}</p>
          <h3 className="font-semibold text-card-foreground mt-1">
            {post.title || "Untitled"}
          </h3>
        </div>
        {isAuthor && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        )}      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-3">
          {post.content ?? ""}
        </p>
        {post.image_url && (
          <img
            src={post.image_url}
            alt=""
            className="mt-2 rounded-md max-h-48 object-cover w-full"
          />
        )}
      </CardContent>
      <CardFooter className="flex items-center gap-4 pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLike}
          disabled={toggleLikeMutation.isPending}
          className={userLiked ? "text-destructive" : ""}
        >
          ♥ {likesCount}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleExpand}
        >
          💬 {post.comments_count} · {isExpanded ? "Hide" : "Show"} comments
        </Button>
      </CardFooter>
      {isExpanded && (
        <CardContent className="border-t pt-4">
          <CommentSection
            postId={post.id}
            userId={userId}
            accessToken={accessToken}
          />
        </CardContent>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => { if (!open) setShowDeleteDialog(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The post will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setShowDeleteDialog(false);
                deleteMutation.mutate(
                  { postId: post.id, userId, accessToken },
                  { onSuccess: (r: { success?: boolean } | undefined) => r?.success && onDeleted() }
                );
              }}
            >
              Delete Post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function CommentSection({
  postId,
  userId,
  accessToken,
}: {
  postId: string;
  userId: string;
  accessToken: string;
}) {
  const [newContent, setNewContent] = useState("");
  const { data: comments = [], isLoading, error, refetch } = useComments(postId);
  const createCommentMutation = useCreateComment(postId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContent.trim()) return;
    createCommentMutation.mutate(
      { content: newContent.trim(), userId, accessToken },
      {
        onSuccess: () => setNewContent(""),
        onSettled: () => refetch(),
      }
    );
  };

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-foreground">Comments</h4>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="text"
          placeholder="Write a comment…"
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          className="flex-1 text-sm"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!newContent.trim() || createCommentMutation.isPending}
        >
          {createCommentMutation.isPending ? "…" : "Post"}
        </Button>
      </form>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading comments…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load comments"}
        </p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No comments yet.</p>
      ) : (
        <ul className="space-y-3">
          {(comments as Comment[]).map((c) => (
            <li key={c.id} className="text-sm">
              <span className="font-medium text-foreground">
                {c.author?.full_name || c.author?.username || "Someone"}
              </span>
              <span className="text-muted-foreground">
                {" "}
                · {new Date(c.created_at).toLocaleDateString()}
              </span>
              <p className="text-muted-foreground mt-0.5">{c.content}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
